import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { cn } from '#/utils/cn';

import type { CategoryNavigationItem } from './types';

type CategoryNavigationItemsListVariant =
  | 'desktopCards'
  | 'desktopLinks'
  | 'mobileCards'
  | 'mobileLinks';

interface CategoryNavigationItemsListProps {
  items: CategoryNavigationItem[];
  activeHref?: string;
  variant: CategoryNavigationItemsListVariant;
  columnsClass?: string;
  extraItem?: ReactNode;
}

const listVariantClasses: Record<
  CategoryNavigationItemsListVariant,
  {
    listClass: string;
    itemClass?: string;
    linkClass: string;
    activeClass: string;
    inactiveClass: string;
    labelClass?: string;
    descriptionClass?: string;
  }
> = {
  desktopCards: {
    listClass: 'grid gap-x-24 gap-y-20',
    linkClass: 'rounded-12 block px-8 py-6 transition-colors',
    activeClass: 'bg-blue-10',
    inactiveClass: 'hover:bg-steel-10',
    labelClass: 'text-fg-header text-static-18 block font-semibold',
    descriptionClass: 'text-slate-60 text-static-14 mt-6 block leading-snug',
  },
  desktopLinks: {
    listClass: 'grid gap-x-16 gap-y-12',
    linkClass:
      'text-static-14 block leading-normal font-normal transition-colors',
    activeClass: 'text-fg-header',
    inactiveClass: 'text-fg-header hover:text-link-fg',
  },
  mobileCards: {
    listClass: 'w-full',
    itemClass: 'border-gray-30 border-b last:border-b-0',
    linkClass: 'block px-12 py-20 transition-colors',
    activeClass: 'bg-blue-10',
    inactiveClass: 'hover:bg-steel-10',
    labelClass: 'text-fg-header text-static-18 block font-semibold',
    descriptionClass:
      'text-slate-60 text-static-16 mt-8 block leading-normal font-normal',
  },
  mobileLinks: {
    listClass:
      'text-fg-subheader text-static-14 w-full px-12 py-20 leading-normal',
    itemClass: 'pt-12 first:pt-0',
    linkClass: 'block font-normal transition-colors',
    activeClass: 'text-fg-subheader font-semibold',
    inactiveClass: 'hover:text-link-fg',
  },
};

export function CategoryNavigationItemsList({
  items,
  activeHref,
  variant,
  columnsClass,
  extraItem,
}: CategoryNavigationItemsListProps) {
  const variantClasses = listVariantClasses[variant];

  return (
    <ul
      className={cn(
        variantClasses.listClass,
        columnsClass ??
          (variant === 'desktopCards' ? 'lg:grid-cols-3' : undefined),
      )}
    >
      {items.map((item) => {
        const isActive = activeHref !== undefined && item.href === activeHref;

        return (
          <li key={item.href} className={variantClasses.itemClass}>
            <Link
              to={item.href}
              onClick={item.onClick}
              aria-current={isActive ? 'page' : undefined}
              data-track="category_nav_item"
              data-track-label={item.label}
              data-track-context="category_nav"
              className={cn(
                variantClasses.linkClass,
                isActive
                  ? variantClasses.activeClass
                  : variantClasses.inactiveClass,
              )}
            >
              <span className={variantClasses.labelClass}>{item.label}</span>
              {item.description && variantClasses.descriptionClass ? (
                <span className={variantClasses.descriptionClass}>
                  {item.description}
                </span>
              ) : null}
            </Link>
          </li>
        );
      })}
      {extraItem ? (
        <li className={variantClasses.itemClass}>{extraItem}</li>
      ) : null}
    </ul>
  );
}

export function CategoryNavigationCta({
  cta,
  variant = 'desktop',
}: {
  cta?: CategoryNavigationItem;
  variant?: 'desktop' | 'mobile';
}) {
  if (!cta) {
    return null;
  }

  const isMobile = variant === 'mobile';

  return (
    <a
      href={cta.href}
      onClick={cta.onClick}
      target={cta.target}
      rel={cta.target === '_blank' ? 'noopener noreferrer' : undefined}
      data-track="category_nav_cta"
      data-track-label={cta.label}
      data-track-context="category_nav"
      className={cn(
        'rounded-12 hover:bg-steel-10 block transition-colors',
        isMobile ? 'px-12 py-20' : 'px-8 py-6',
      )}
    >
      <span className="block transition-colors">
        <span className="text-static-18 flex items-center gap-6 font-semibold">
          <span>{cta.label}</span>
          <span className="inline-flex size-14 items-center justify-center rounded-[3px] border-2 border-current text-[10px] leading-none font-bold">
            ↗
          </span>
        </span>
        {cta.description ? (
          <span
            className={cn(
              'text-slate-60 block',
              isMobile
                ? 'text-static-16 mt-8 leading-normal font-normal'
                : 'text-static-14 mt-6 leading-snug',
            )}
          >
            {cta.description}
          </span>
        ) : null}
      </span>
    </a>
  );
}
